import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Stato locale dello stream "Oggi": item rimandati (snooze) e FYI archiviate.
 *
 * Persistito in localStorage ('masamune-action-stream'): lo snooze sopravvive
 * al refresh ma resta per-browser — è una preferenza personale dell'utente,
 * non stato di dominio (nessun item viene davvero chiuso da qui).
 *
 * Le chiavi degli item sono nel formato `entityType:id` (vedi useActionStream).
 */

type ActionBand = 0 | 1 | 2 | 3;

interface ActionStreamState {
  /** key → ISO datetime fino a cui l'item resta nascosto. */
  snoozed: Record<string, string>;
  /** Chiavi delle notifiche FYI (band 3) archiviate definitivamente. */
  archivedFyi: string[];
  /**
   * Rimanda un item fino a `untilISO`. Gli item di band 0 (critici) non
   * possono sparire oltre domani alle 09:00: la scadenza viene clampata.
   */
  snooze: (key: string, untilISO: string, band?: ActionBand) => void;
  /** Risveglia subito un item rimandato. */
  wake: (key: string) => void;
  /** Archivia in blocco le notifiche FYI indicate. */
  archiveFyi: (keys: string[]) => void;
  /** True se l'item è ancora rimandato a `now` (gli snooze nel passato auto-scadono). */
  isSnoozed: (key: string, now?: number) => boolean;
}

/** Domani alle 09:00 locali — tetto massimo di snooze per gli item critici (band 0). */
function tomorrowAt9(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}

/**
 * Rimuove le voci di snooze già scadute. Pulizia opportunistica eseguita
 * a ogni write (snooze/wake): `isSnoozed` resta una pura lettura — niente
 * set durante il render — e il localStorage non accumula chiavi morte.
 */
function pruneExpired(snoozed: Record<string, string>, now: number): Record<string, string> {
  const next: Record<string, string> = {};
  for (const [k, v] of Object.entries(snoozed)) {
    if (new Date(v).getTime() > now) next[k] = v;
  }
  return next;
}

export const useActionStreamStore = create<ActionStreamState>()(
  persist(
    (set, get) => ({
      snoozed: {},
      archivedFyi: [],
      snooze: (key, untilISO, band) => {
        const now = Date.now();
        let until = new Date(untilISO);
        if (Number.isNaN(until.getTime())) return; // data non valida: no-op difensivo
        if (band === 0) {
          const cap = tomorrowAt9();
          if (until.getTime() > cap.getTime()) until = cap;
        }
        if (until.getTime() <= now) return; // snooze nel passato: inutile
        set({ snoozed: { ...pruneExpired(get().snoozed, now), [key]: until.toISOString() } });
      },
      wake: (key) => {
        const next = pruneExpired(get().snoozed, Date.now());
        delete next[key];
        set({ snoozed: next });
      },
      archiveFyi: (keys) => {
        if (!keys.length) return;
        set({ archivedFyi: Array.from(new Set([...get().archivedFyi, ...keys])) });
      },
      isSnoozed: (key, now = Date.now()) => {
        const until = get().snoozed[key];
        return !!until && new Date(until).getTime() > now;
      },
    }),
    { name: 'masamune-action-stream' },
  ),
);
